/**
 * This component is part of the new UI design (v2).
 *
 * This component renders the head section of transaction-body items.
 * It displays the following details:
 * 1) Code
 * 2) Description
 * 3) Evidence
 */

import React from 'react';
import { cmxTypeguards, commonEnums } from '@codametrix/ui-common';
import classnames from 'classnames';
import {
  CMXIcon,
  Conditional,
  MedicalCodeChip
} from '@codametrix/ui-components';

/**
 * React component that displays a Header
 * @param medicalCode
 * @param techTransaction
 * @param errors
 * @param invoice
 * @param codeEdits
 * @returns
 */
export const MedicalCodeAccordionHeader = (
  medicalCode: CMxCommonApp.ChargeSessionCode | CMxCommonApp.ZippedCodeBlock,
  techTransaction: CMxCommonApp.TechTransaction,
  errors: AmplifyAPI.AmplifyError[],
  invoice: CMxCommonApp.CodableInvoice,
  codeEdits: CMxCommonApp.CodeEditInputs
) => {
  if (cmxTypeguards.isZippedCodeBlock(medicalCode)) {
    const zippedCode = medicalCode as CMxCommonApp.ZippedCodeBlock;
    const sharedInvoice =
      invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES;
    return (
      <div className="medical-code-accordion-header icd-code transaction-shared-code-list">
        <div className="move-column">
          <span className="code-gripper prevent-click">
            <CMXIcon
              svgIconName="BiomeGripperSVG20"
              svgIconSize="20"
              svgIconClass="gripper"
            />
          </span>
        </div>
        <div className="codes-column">
          {zippedCode.codes.map((code) => {
            const active = code.active;
            return (
              <span
                key={code.code}
                className="icd-code unclickable prevent-click"
                data-dd-action-name={
                  active === true ? '~Toggle code off' : '~Toggle code on'
                }>
                <MedicalCodeChip
                  code={code.code}
                  codeType={code.codeType}
                  editable={false}
                  shared={true}
                  isSharedInvoice={sharedInvoice}
                  active={active}
                  classNames={['code-chip']}
                />
              </span>
            );
          })}
        </div>
        {/* no descriptions for shared codes */}
        {/* no actions for shared codes */}
      </div>
    );
  } else if (cmxTypeguards.isChargeSessionCode(medicalCode)) {
    const sessionCode = medicalCode as CMxCommonApp.ChargeSessionCode;
    //@ts-ignore
    const shared = sessionCode.shared ?? false;
    const sharedInvoice =
      invoice?.type === commonEnums.InvoiceTypes.SHARED_CODES;
    const uneditableSharedCode = shared && !sharedInvoice;

    const styleClasses = [
      `medical-code-accordion-header`,
      {
        'code-errors': errors.some(
          (error) =>
            (error.message_key === 'procedure_code.units.invalid' &&
              sessionCode.codeType === 'procedure') ||
            error.message_key !== 'procedure_code.units.invalid'
        ),
        unitable: sessionCode.unitable,
        editable: sessionCode.editable,
        'uneditable-shared-code': uneditableSharedCode,
        ['transaction-shared-code']: shared
      },
      `${sessionCode.provenance.toLowerCase()}-code`
    ];

    const evidence = sessionCode.evidence ?? [];
    const hasEvidence = evidence.length > 0;
    const isProcedureCode = sessionCode.codeType === 'procedure';
    const description = sessionCode.description || 'Retrieving description...';
    const codeUnitsStyle = [
      'code-units-input',
      {
        'procedure-code-units-invalid': errors.some(
          (error) => error.message_key === 'procedure_code.units.invalid'
        )
      }
    ];

    return (
      <div
        key={`${sessionCode.code}-${sessionCode.codeType}`}
        data-code={`${sessionCode.code}`}
        data-code-type={`${sessionCode.codeType}`}
        className={classnames(styleClasses)}>
        <div className="units-column">
          <Conditional on={!isProcedureCode}>
            <span className="code-gripper prevent-click">
              <CMXIcon
                svgIconName="BiomeGripperSVG20"
                svgIconSize="20"
                svgIconClass="gripper"
              />
            </span>
          </Conditional>
        </div>
        <div className="codes-column">
          <MedicalCodeChip
            code={sessionCode.code}
            codeType={sessionCode.codeType}
            editable={false}
            shared={shared}
            isSharedInvoice={sharedInvoice}
            active={sessionCode.active}
            codeEdits={codeEdits}
            classNames={[`${sessionCode.provenance.toLowerCase()}-code`]}
          />
        </div>
        <div
          title={description}
          className="qa-description-column description-column">
          <span className="ellipsify">{description}</span>
        </div>
        <div className="code-actions-column">
          {hasEvidence && (
            <div className="col-evidence code-indicator-cell">
              <span className="indicator">CMX</span>
            </div>
          )}
        </div>
      </div>
    );
  }
};
